import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tokenStorage from '../../utils/tokenStorage';
import authService from '../../services/authService';

// Async thunks for API calls
export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      // Get token from token storage
      const token = tokenStorage.getAccessToken();
      const refreshToken = tokenStorage.getRefreshToken();

      // Also check cookies as fallback
      const hasCookieAccessToken = document.cookie.includes('accessToken=');
      const hasCookieRefreshToken = document.cookie.includes('refreshToken=');

      // Helper to validate token format (basic JWT check)
      const isValidTokenFormat = token => {
        if (!token) return false;
        try {
          const parts = token.split('.');
          if (parts.length !== 3) return false;
          // Try to decode the payload
          JSON.parse(atob(parts[1]));
          return true;
        } catch {
          return false;
        }
      };

      // Validate tokens before using them
      const hasValidAccessToken = (token && isValidTokenFormat(token)) || hasCookieAccessToken;
      const hasValidRefreshToken =
        (refreshToken && isValidTokenFormat(refreshToken)) || hasCookieRefreshToken;

      // Only call /api/auth/me if we have a valid access token (localStorage or cookie)
      // Don't call the API if there's no valid token at all
      if (hasValidAccessToken) {
        try {
          // Try to get user data with current token
          const data = await authService.me();

          // Check if the response indicates failure
          if (data.success === false) {
            // Check if it's a connection error
            const errorMsg = data.error || data.message || '';
            if (
              errorMsg.includes('Backend server not available') ||
              errorMsg.includes('Cannot connect')
            ) {
              console.warn('Backend not available, clearing tokens', errorMsg);
              tokenStorage.clearTokens();
              throw new Error('Backend server not available');
            }
            throw new Error(errorMsg || 'Failed to get user data');
          }

          return data.data?.user || data.user;
        } catch (error) {
          // Check if it's a connection error - if so, clear tokens and fail fast
          const errorMsg = error.message || String(error);
          const errorString = JSON.stringify(error).toLowerCase();
          const isConnectionError =
            errorMsg.includes('ERR_CONNECTION_REFUSED') ||
            errorMsg.includes('Failed to fetch') ||
            errorMsg.includes('NetworkError') ||
            errorMsg.includes('ECONNREFUSED') ||
            errorMsg.includes('Backend server not available') ||
            errorMsg.includes('Cannot connect') ||
            errorString.includes('connection_refused') ||
            errorString.includes('failed to fetch') ||
            errorString.includes('networkerror');

          if (isConnectionError) {
            console.warn('Backend not available (connection error), clearing tokens', errorMsg);
            tokenStorage.clearTokens();
            throw new Error('Backend server not available');
          }

          // If /me fails, try refresh if we have a valid refresh token
          if (hasValidRefreshToken) {
            try {
              const refreshData = await authService.refresh();

              // Check if the response indicates failure
              if (refreshData.success === false) {
                const refreshErrorMsg = refreshData.error || refreshData.message || '';
                // If it's a connection error, clear tokens immediately
                if (
                  refreshErrorMsg.includes('Backend server not available') ||
                  refreshErrorMsg.includes('Cannot connect')
                ) {
                  console.warn(
                    'Backend not available during refresh, clearing tokens',
                    refreshErrorMsg
                  );
                  tokenStorage.clearTokens();
                  throw new Error('Backend server not available');
                }
                throw new Error(refreshErrorMsg || 'Token refresh failed');
              }

              if (refreshData.tokens) {
                // Store new tokens using token storage
                tokenStorage.setAccessToken(refreshData.tokens.accessToken);
                if (refreshData.tokens.refreshToken) {
                  tokenStorage.replaceRefreshToken(refreshData.tokens.refreshToken);
                }
              }
              return refreshData.data?.user || refreshData.user;
            } catch (refreshError) {
              // If refresh fails, check if it's a connection error or auth error
              const errorMsg = refreshError.message || String(refreshError);
              const isConnectionError =
                errorMsg.includes('ERR_CONNECTION_REFUSED') ||
                errorMsg.includes('Failed to fetch') ||
                errorMsg.includes('NetworkError') ||
                errorMsg.includes('ECONNREFUSED');

              // Clear tokens on connection/auth errors to prevent retries
              if (
                isConnectionError ||
                errorMsg.includes('401') ||
                errorMsg.includes('Unauthorized') ||
                errorMsg.includes('Invalid')
              ) {
                console.warn(
                  'Refresh token failed (connection/auth error), clearing tokens',
                  errorMsg
                );
                tokenStorage.clearTokens();
              }
              throw new Error('Token validation failed');
            }
          } else {
            // No refresh token available, check if access token is still valid
            if (token) {
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const now = Math.floor(Date.now() / 1000);
                if (payload.exp < now) {
                  throw new Error('Access token expired');
                }
                // Token is still valid, but server rejected it - might be a server issue
                throw new Error('Token validation failed');
              } catch (error) {
                throw new Error('Access token expired or invalid');
              }
            } else {
              throw new Error('No authentication token found');
            }
          }
        }
      } else if (hasValidRefreshToken) {
        // No access token, but we have a valid refresh token - try refresh
        try {
          const refreshData = await authService.refresh();

          // Check if the response indicates failure
          if (refreshData.success === false) {
            const refreshErrorMsg = refreshData.error || refreshData.message || '';
            // If it's a connection error, clear tokens immediately
            if (
              refreshErrorMsg.includes('Backend server not available') ||
              refreshErrorMsg.includes('Cannot connect')
            ) {
              console.warn(
                'Backend not available during refresh, clearing tokens',
                refreshErrorMsg
              );
              tokenStorage.clearTokens();
              throw new Error('Backend server not available');
            }
            throw new Error(refreshErrorMsg || 'Token refresh failed');
          }

          if (refreshData.tokens) {
            // Store new tokens using token storage
            tokenStorage.setAccessToken(refreshData.tokens.accessToken);
            if (refreshData.tokens.refreshToken) {
              tokenStorage.replaceRefreshToken(refreshData.tokens.refreshToken);
            }
          }
          return refreshData.data?.user || refreshData.user;
        } catch (refreshError) {
          // If refresh fails (401, invalid, or connection error), clear tokens to prevent retries
          console.warn('Refresh token failed, clearing tokens', refreshError.message);
          tokenStorage.clearTokens();
          throw new Error('Token refresh failed');
        }
      } else {
        // No tokens at all - don't make any API calls
        throw new Error('No authentication token found');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password, remember }, { rejectWithValue }) => {
    try {
      const data = await authService.login(email, password, remember);

      // Check if the response indicates failure
      if (data.success === false) {
        return rejectWithValue(data.error || data.message || 'Login failed');
      }

      // Store tokens using token storage
      // handleApiResponse extracts data, so tokens should be at data.tokens
      // But also check data.data?.tokens for backward compatibility
      const tokens = data.tokens || data.data?.tokens;

      console.log('🔑 [authSlice] Login response:', {
        dataKeys: Object.keys(data || {}),
        hasTokens: !!tokens,
        hasAccessToken: !!tokens?.accessToken,
        hasRefreshToken: !!tokens?.refreshToken,
        tokensPreview: tokens
          ? {
              accessTokenLength: tokens.accessToken?.length || 0,
              refreshTokenLength: tokens.refreshToken?.length || 0,
              accessTokenPreview: tokens.accessToken
                ? `${tokens.accessToken.substring(0, 20)}...`
                : null,
              refreshTokenPreview: tokens.refreshToken
                ? `${tokens.refreshToken.substring(0, 20)}...`
                : null,
            }
          : null,
        fullDataStructure: JSON.stringify(data, null, 2).substring(0, 500),
      });

      if (tokens) {
        if (tokens.accessToken) {
          tokenStorage.setAccessToken(tokens.accessToken);
          console.log('✅ [authSlice] Access token stored in localStorage');
          // Verify it was stored
          const stored = tokenStorage.getAccessToken();
          console.log(
            '🔍 [authSlice] Verified stored access token:',
            stored ? `${stored.substring(0, 20)}...` : 'NOT FOUND'
          );
        } else {
          console.warn('⚠️ [authSlice] No access token in response');
        }
        if (tokens.refreshToken) {
          tokenStorage.setRefreshToken(tokens.refreshToken);
          console.log('✅ [authSlice] Refresh token stored in localStorage');
          // Verify it was stored
          const stored = tokenStorage.getRefreshToken();
          console.log(
            '🔍 [authSlice] Verified stored refresh token:',
            stored ? `${stored.substring(0, 20)}...` : 'NOT FOUND'
          );
        } else {
          console.warn('⚠️ [authSlice] No refresh token in response');
        }
      } else {
        console.error('❌ [authSlice] No tokens in login response');
        console.error('   Full response data:', JSON.stringify(data, null, 2));
      }

      const user = data.user || data.data?.user;
      return { user };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ name, email, password, contact, recaptchaToken }, { rejectWithValue }) => {
    try {
      const data = await authService.register({
        name,
        email,
        password,
        contact,
        recaptchaToken,
      });

      // Mirror login behavior: do not treat error payloads as success.
      if (data.success === false) {
        return rejectWithValue(data.error || data.message || 'Registration failed');
      }

      // Keep register flow aligned with login flow: store tokens if returned.
      // This avoids immediate auth flip to false when checkAuthStatus runs.
      const tokens = data.tokens || data.data?.tokens;
      if (tokens) {
        if (tokens.accessToken) {
          tokenStorage.setAccessToken(tokens.accessToken);
        }
        if (tokens.refreshToken) {
          tokenStorage.setRefreshToken(tokens.refreshToken);
        }
      }

      return { user: data.data?.user || data.user };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      // Check if we have a valid refresh token before making the API call
      const refreshTokenFromStorage = tokenStorage.getRefreshToken();
      const hasCookieRefreshToken = document.cookie.includes('refreshToken=');

      // Helper to validate token format (basic JWT check)
      const isValidTokenFormat = token => {
        if (!token) return false;
        try {
          const parts = token.split('.');
          if (parts.length !== 3) return false;
          // Try to decode the payload
          JSON.parse(atob(parts[1]));
          return true;
        } catch {
          return false;
        }
      };

      const hasValidRefreshToken =
        (refreshTokenFromStorage && isValidTokenFormat(refreshTokenFromStorage)) ||
        hasCookieRefreshToken;

      if (!hasValidRefreshToken) {
        console.warn('⚠️ [authSlice] No valid refresh token found, cannot refresh');
        throw new Error('No valid refresh token found');
      }

      console.log('🔄 [authSlice] Attempting token refresh...');
      const data = await authService.refresh();

      // Check if the response indicates failure
      if (data.success === false) {
        console.error('❌ [authSlice] Token refresh failed:', data.error || data.message);
        throw new Error(data.error || data.message || 'Token refresh failed');
      }

      // Store new tokens using token storage (handle both response formats)
      const tokens = data.tokens || data.data?.tokens;
      if (tokens) {
        if (tokens.accessToken) {
          tokenStorage.setAccessToken(tokens.accessToken);
          console.log('✅ [authSlice] New access token stored');
        }
        if (tokens.refreshToken) {
          tokenStorage.replaceRefreshToken(tokens.refreshToken);
          console.log('✅ [authSlice] New refresh token stored');
        }
      } else {
        console.warn('⚠️ [authSlice] No tokens in refresh response');
      }

      const user = data.user || data.data?.user;
      console.log('✅ [authSlice] Token refresh successful');
      return { user };
    } catch (error) {
      // If refresh fails, check if it's a connection error or auth error
      const errorMsg = error.message || String(error);
      const errorString = JSON.stringify(error).toLowerCase();
      const isConnectionError =
        errorMsg.includes('ERR_CONNECTION_REFUSED') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ECONNREFUSED') ||
        errorString.includes('connection_refused') ||
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror');

      // Clear tokens on connection/auth errors to prevent retries
      if (
        isConnectionError ||
        errorMsg.includes('401') ||
        errorMsg.includes('Unauthorized') ||
        errorMsg.includes('Invalid') ||
        errorMsg.includes('No valid refresh token')
      ) {
        console.warn('❌ [authSlice] Refresh token failed, clearing tokens:', errorMsg);
        tokenStorage.clearTokens();
      }
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (userData, { rejectWithValue }) => {
    try {
      const data = await authService.updateProfile(userData);
      return data.data?.user || data.user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { rejectWithValue }) => {
  try {
    const data = await authService.logout();

    // Clear tokens using token storage
    tokenStorage.clearTokens();

    return data;
  } catch (error) {
    // Clear tokens even if logout fails
    tokenStorage.clearTokens();
    return rejectWithValue(error.message);
  }
});

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      // Cookies are cleared by the server on logout
    },
    clearError: state => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    // Initialize auth state - now handled by checkAuthStatus thunk
    initializeAuth: () => {
      // No longer needed - authentication is handled by server-side cookies
      // The checkAuthStatus thunk will be called to verify authentication
    },
  },
  extraReducers: builder => {
    builder
      // Check auth status
      .addCase(checkAuthStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // Login
      .addCase(loginUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // Refresh token
      .addCase(refreshToken.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // Update profile
      .addCase(updateUserProfile.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, setLoading, initializeAuth } = authSlice.actions;

// Selectors
export const selectAuth = state => state.auth;
export const selectUser = state => state.auth.user;
export const selectIsAuthenticated = state => state.auth.isAuthenticated;
export const selectAuthLoading = state => state.auth.loading;
export const selectAuthError = state => state.auth.error;

// Helper function to get auth headers (memoized to prevent infinite calls)
let cachedToken = null;
let lastTokenCheck = 0;
const TOKEN_CACHE_DURATION = 1000; // 1 second cache

export const getAuthHeaders = () => {
  const now = Date.now();

  // Use cached token if it's still fresh
  if (cachedToken && now - lastTokenCheck < TOKEN_CACHE_DURATION) {
    return cachedToken ? { Authorization: `Bearer ${cachedToken}` } : {};
  }

  // Get fresh token and cache it
  const token = tokenStorage.getAccessToken();
  cachedToken = token;
  lastTokenCheck = now;

  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to clear token cache (call when tokens are updated)
export const clearTokenCache = () => {
  cachedToken = null;
  lastTokenCheck = 0;
};

// Make clearTokenCache available globally for tokenStorage
if (typeof window !== 'undefined') {
  window.clearTokenCache = clearTokenCache;
}

// Helper function for authenticated requests
export const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = tokenStorage.getAccessToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Token expired or invalid - this will be handled by the auth middleware
    throw new Error('Authentication required');
  }

  return response;
};

export default authSlice.reducer;
